import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { KPICard, KPIGrid } from '../../components/admin/KPICards';
import { 
  FileText, Phone, Mail, MapPin, Calendar, Clock, User,
  CheckCircle, XCircle, AlertCircle, TrendingUp, Filter,
  ChevronRight, MessageCircle, Building2, Search
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  nueva_solicitud: { label: 'Nueva solicitud', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: AlertCircle },
  contactado: { label: 'Contactado', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Phone },
  prueba_agendada: { label: 'Prueba Agendada', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Calendar },
  instalado: { label: 'Instalado', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  perdido: { label: 'Perdido', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30', icon: XCircle }
};

const EQUIPMENT_MODELS = ['MANKLAR KAJAAL', 'MANKLAR TIKAL', 'MANKLAR TIKAL S', 'MANKLAR CHAAK'];

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.nueva_solicitud;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

function LeadCard({ lead, onUpdateStatus, onContact }) {
  const handleWhatsApp = () => {
    if (lead.phone) {
      const message = encodeURIComponent(`Hola ${lead.name}, te contactamos desde Manklar respecto a tu solicitud de prueba gratuita.`);
      window.open(`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
    }
  };

  const handleCall = () => {
    if (lead.phone) {
      window.open(`tel:${lead.phone.replace(/\s/g, '')}`);
    }
  };

  const handleEmail = () => {
    if (lead.email) {
      window.open(`mailto:${lead.email}?subject=Manklar - Tu solicitud de prueba gratuita`);
    }
  };

  return (
    <div 
      className="p-5 rounded-xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-all duration-300 group"
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center">
            <span className="text-lg font-light text-white">
              {lead.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h3 className="text-base font-medium text-white">{lead.name}</h3>
            <p className="text-xs text-zinc-500">
              {new Date(lead.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          </div>
        </div>
        <StatusBadge status={lead.status} />
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <Phone className="w-3.5 h-3.5 text-zinc-600" />
          <span>{lead.phone || 'Sin teléfono'}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <Mail className="w-3.5 h-3.5 text-zinc-600" />
          <span className="truncate">{lead.email}</span>
        </div>
        {lead.address && (
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <MapPin className="w-3.5 h-3.5 text-zinc-600" />
            <span className="truncate">{lead.address}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <Building2 className="w-3.5 h-3.5 text-zinc-600" />
          <span>{lead.source === 'referral_landing' ? `Referido por ${lead.referrer_name}` : 'Landing directa'}</span>
        </div>
      </div>

      {lead.commercial_agent && (
        <div className="mb-4 p-2 rounded bg-white/5 text-xs text-zinc-400">
          <User className="w-3 h-3 inline mr-1" />
          Comercial: {lead.commercial_agent}
        </div>
      )}

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleWhatsApp}
          className="flex-1 h-9 bg-[#25D366]/10 border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/20"
        >
          <MessageCircle className="w-4 h-4 mr-1" />
          WhatsApp
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCall}
          className="h-9 bg-transparent border-white/10 text-zinc-300 hover:bg-white/10"
        >
          <Phone className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleEmail}
          className="h-9 bg-transparent border-white/10 text-zinc-300 hover:bg-white/10"
        >
          <Mail className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          onClick={() => onUpdateStatus(lead)}
          className="h-9 bg-white text-black hover:bg-zinc-200"
        >
          Gestionar
        </Button>
      </div>
    </div>
  );
}

function UpdateStatusModal({ lead, isOpen, onClose, onUpdate }) {
  const [status, setStatus] = useState(lead?.status || 'nueva_solicitud');
  const [notes, setNotes] = useState('');
  const [commercialAgent, setCommercialAgent] = useState(lead?.commercial_agent || '');
  const [scheduledDate, setScheduledDate] = useState('');
  const [equipmentModel, setEquipmentModel] = useState('MANKLAR TIKAL');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (lead) {
      setStatus(lead.status);
      setCommercialAgent(lead.commercial_agent || '');
    }
  }, [lead, setStatus, setCommercialAgent]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/leads/${lead.id}/status`,
        {
          status,
          notes: notes || undefined,
          commercial_agent: commercialAgent || undefined,
          scheduled_date: scheduledDate || undefined,
          equipment_model: status === 'instalado' ? equipmentModel : undefined
        },
        { withCredentials: true }
      );
      toast.success('Solicitud actualizada correctamente');
      onUpdate();
      onClose();
    } catch (err) {
      toast.error('Error al actualizar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-['Outfit']">
            Gestionar Solicitud
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="p-4 rounded-lg bg-white/5 mb-4">
            <p className="text-white font-medium">{lead.name}</p>
            <p className="text-sm text-zinc-400">{lead.email}</p>
            <p className="text-sm text-zinc-400">{lead.phone}</p>
          </div>

          <div>
            <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
              Estado
            </label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-full h-11 bg-white/5 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key} className="text-zinc-300 focus:text-white focus:bg-white/10">
                    {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
              Comercial Asignado
            </label>
            <Input
              value={commercialAgent}
              onChange={(e) => setCommercialAgent(e.target.value)}
              placeholder="Nombre del comercial"
              className="h-11 bg-white/5 border-white/10 text-white"
            />
          </div>

          {(status === 'prueba_agendada' || status === 'instalado') && (
            <div>
              <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                {status === 'prueba_agendada' ? 'Fecha de Prueba' : 'Fecha de Instalación'}
              </label>
              <Input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                className="h-11 bg-white/5 border-white/10 text-white"
              />
            </div>
          )}

          {status === 'instalado' && (
            <div>
              <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                Modelo Instalado
              </label>
              <Select value={equipmentModel} onValueChange={setEquipmentModel}>
                <SelectTrigger className="w-full h-11 bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {EQUIPMENT_MODELS.map((model) => (
                    <SelectItem key={model} value={model} className="text-zinc-300 focus:text-white focus:bg-white/10">
                      {model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
              Notas
            </label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Añadir nota..."
              className="h-11 bg-white/5 border-white/10 text-white"
            />
          </div>
        </div>

        <DialogFooter className="gap-3">
          <Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-white">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-white text-black hover:bg-zinc-200">
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminLeads() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/leads`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/leads/stats`, { withCredentials: true })
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleUpdateStatus = (lead) => {
    setSelectedLead(lead);
    setShowModal(true);
  };

  // Filter by status and search query
  const filteredLeads = leads.filter(l => {
    const matchesStatus = filterStatus === 'all' || l.status === filterStatus;
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch = !query || 
      l.name?.toLowerCase().includes(query) ||
      l.email?.toLowerCase().includes(query) ||
      l.phone?.toLowerCase().includes(query);
    return matchesStatus && matchesSearch;
  });

  const newLeadsCount = stats?.nueva_solicitud || 0;

  if (loading) {
    return (
      <AdminLayout
        title="Solicitudes"
        subtitle="Cargando..."
        onLogout={handleLogout}
      >
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Solicitudes de Prueba Gratuita"
      subtitle={`${leads.length} solicitudes en total`}
      pendingRewards={newLeadsCount}
      onLogout={handleLogout}
    >
      {/* Stats */}
      <KPIGrid className="mb-8">
        <KPICard
          title="Nuevas Solicitudes"
          value={stats?.nueva_solicitud || 0}
          icon={AlertCircle}
          variant="accent"
        />
        <KPICard
          title="Pruebas Agendadas"
          value={stats?.prueba_agendada || 0}
          icon={Calendar}
        />
        <KPICard
          title="Instalaciones"
          value={stats?.instalado || 0}
          icon={CheckCircle}
          variant="success"
        />
        <KPICard
          title="Tasa de Conversión"
          value={`${stats?.conversion_rate || 0}%`}
          icon={TrendingUp}
          variant="highlight"
        />
      </KPIGrid>

      {/* Pending Counter */}
      {newLeadsCount > 0 && (
        <div className="mb-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center gap-4">
          <AlertCircle className="w-6 h-6 text-blue-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-400">
              Tienes {newLeadsCount} solicitud{newLeadsCount > 1 ? 'es' : ''} pendiente{newLeadsCount > 1 ? 's' : ''} de gestionar
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => setFilterStatus('nueva_solicitud')}
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            Ver nuevas
          </Button>
        </div>
      )}

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
          {/* Search */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              type="text"
              placeholder="Buscar por nombre, email, teléfono..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9 bg-white/5 border-white/10 text-white text-sm placeholder:text-zinc-500"
              data-testid="leads-search-input"
            />
          </div>
          
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48 h-9 bg-white/5 border-white/10 text-white text-sm" data-testid="leads-status-filter">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="all" className="text-zinc-300">Todas</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key} className="text-zinc-300">
                    {config.label} ({stats?.[key] || 0})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-sm text-zinc-500">{filteredLeads.length} resultado{filteredLeads.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Leads Grid */}
      {filteredLeads.length === 0 ? (
        <div className="p-12 rounded-xl text-center bg-white/[0.02] border border-white/10">
          <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg text-white mb-2 font-['Outfit']">
            No hay solicitudes
          </h3>
          <p className="text-sm text-zinc-500">
            Las nuevas solicitudes aparecerán aquí
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredLeads.map(lead => (
            <LeadCard 
              key={lead.id} 
              lead={lead} 
              onUpdateStatus={handleUpdateStatus}
            />
          ))}
        </div>
      )}

      {/* Update Status Modal */}
      <UpdateStatusModal
        lead={selectedLead}
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedLead(null);
        }}
        onUpdate={fetchData}
      />
    </AdminLayout>
  );
}
